import {
  headingFlags,
  type Span,
  splitSentences,
  wordTokens,
} from "@/lib/tool-kit/writing-analysis/text";

/**
 * Sticky sentences: the share of each sentence made of glue words.
 *
 * A glue word is a function word — an article, preposition, auxiliary verb, pronoun or
 * conjunction. It holds a sentence together but carries almost no meaning of its own,
 * so a sentence that is mostly glue ("It is the case that there are a number of things
 * that are of importance") makes the reader wade through grammar to reach the content.
 * Editing software calls such a sentence "sticky" and reports a "glue index" for a whole
 * text; ProWritingAid's help page gives 40% as the target for published writing.
 *
 * The list below is this site's own, built from the closed word classes of English; it
 * is not the list any other tool uses, so percentages are comparable in kind, not to
 * the decimal. Nothing here judges meaning: a short, glue-heavy sentence can be exactly
 * right, which is why the thresholds are the writer's to move.
 */

/** Articles, prepositions, conjunctions, pronouns, auxiliaries, common adverbs. */
export const GLUE_WORDS: ReadonlySet<string> = new Set([
  // articles and determiners
  "a",
  "an",
  "the",
  "this",
  "that",
  "these",
  "those",
  "each",
  "every",
  "any",
  "some",
  "all",
  "both",
  "either",
  "neither",
  "another",
  "such",
  "no",
  // prepositions
  "of",
  "in",
  "on",
  "at",
  "to",
  "by",
  "for",
  "with",
  "from",
  "into",
  "onto",
  "upon",
  "over",
  "under",
  "about",
  "above",
  "below",
  "between",
  "among",
  "through",
  "during",
  "before",
  "after",
  "within",
  "without",
  "against",
  "toward",
  "towards",
  "per",
  "via",
  "as",
  "than",
  "up",
  "out",
  "off",
  // conjunctions and relatives
  "and",
  "or",
  "but",
  "nor",
  "so",
  "yet",
  "if",
  "because",
  "although",
  "though",
  "while",
  "whereas",
  "unless",
  "until",
  "whether",
  "which",
  "who",
  "whom",
  "whose",
  "what",
  "when",
  "where",
  "why",
  "how",
  "then",
  "there",
  "here",
  // pronouns
  "i",
  "me",
  "my",
  "mine",
  "we",
  "us",
  "our",
  "ours",
  "you",
  "your",
  "yours",
  "he",
  "him",
  "his",
  "she",
  "her",
  "hers",
  "it",
  "its",
  "they",
  "them",
  "their",
  "theirs",
  "one",
  "ones",
  "itself",
  "themselves",
  // auxiliaries and the verb "to be"
  "am",
  "is",
  "are",
  "was",
  "were",
  "be",
  "been",
  "being",
  "do",
  "does",
  "did",
  "have",
  "has",
  "had",
  "having",
  "will",
  "would",
  "shall",
  "should",
  "can",
  "could",
  "may",
  "might",
  "must",
  // very common adverbs
  "not",
  "also",
  "just",
  "very",
  "only",
  "even",
  "still",
  "too",
  "more",
  "most",
  "much",
  "many",
  "other",
  "own",
  // contractions of the above
  "it's",
  "that's",
  "there's",
  "here's",
  "what's",
  "he's",
  "she's",
  "i'm",
  "i've",
  "i'd",
  "i'll",
  "we're",
  "we've",
  "we'll",
  "you're",
  "you've",
  "they're",
  "they've",
  "they'll",
  "isn't",
  "aren't",
  "wasn't",
  "weren't",
  "don't",
  "doesn't",
  "didn't",
  "won't",
  "wouldn't",
  "can't",
  "couldn't",
  "shouldn't",
  "hasn't",
  "haven't",
  "hadn't",
]);

const RE_CURLY = /[’‘]/g;
const RE_EDGE_QUOTES = /^'+|'+$/g;

/** The form looked up in the glue list: lowercase, NFC, straight apostrophes. */
export function glueKey(word: string): string {
  return word
    .normalize("NFC")
    .toLowerCase()
    .replace(RE_CURLY, "'")
    .replace(RE_EDGE_QUOTES, "");
}

export function isGlueWord(word: string): boolean {
  return GLUE_WORDS.has(glueKey(word));
}

export type StickyLevel = "sticky" | "semi" | "ok" | "short";

export interface StickyOptions {
  /** Share of glue words (0–1) at which a sentence counts as semi-sticky. */
  semi: number;
  /** Share at which it counts as sticky. */
  sticky: number;
  /** Shorter sentences are counted but never rated: a percentage of 4 words means little. */
  minWords: number;
}

/**
 * Calibrated on this list: edited plain prose (four of this site's own guides, about
 * 6,700 words) has a glue index of 36–40%, its median sentence sits near 40–43%, and
 * only the top few percent of sentences reach 60%. Semi-sticky from 50% and sticky from
 * 60% therefore flags roughly the stickiest quarter and twentieth of ordinary prose.
 */
export const STICKY_PRESETS = {
  strict: { semi: 0.45, sticky: 0.55, minWords: 6 },
  standard: { semi: 0.5, sticky: 0.6, minWords: 6 },
  relaxed: { semi: 0.55, sticky: 0.65, minWords: 6 },
} as const satisfies Record<string, StickyOptions>;

export const DEFAULT_STICKY_OPTIONS: StickyOptions = STICKY_PRESETS.standard;

export interface StickySentence extends Span {
  text: string;
  words: number;
  glue: number;
  /** 0–1. */
  share: number;
  level: StickyLevel;
  /** Each glue word, as offsets into the reader's text. */
  glueSpans: Span[];
  /** The longest unbroken run of glue words, if it is three or more. */
  longestRun: { start: number; end: number; length: number } | null;
}

export interface StickyReport {
  words: number;
  glue: number;
  /** Glue words as a share of all words, 0–1 (the "glue index"). */
  index: number;
  sentences: StickySentence[];
  sticky: number;
  semi: number;
  rated: number;
}

export const MIN_RUN = 3;

function rate(
  share: number,
  words: number,
  options: StickyOptions
): StickyLevel {
  if (words < options.minWords) {
    return "short";
  }
  if (share >= options.sticky) {
    return "sticky";
  }
  return share >= options.semi ? "semi" : "ok";
}

interface Counted {
  words: number;
  glueSpans: Span[];
  longestRun: StickySentence["longestRun"];
}

function countSentence(
  tokens: readonly { text: string; start: number; end: number }[]
): Counted {
  const glueSpans: Span[] = [];
  let longest: { from: number; to: number; length: number } | null = null;
  let run: { from: number; to: number; length: number } | null = null;
  for (const [index, token] of tokens.entries()) {
    if (!isGlueWord(token.text)) {
      run = null;
      continue;
    }
    glueSpans.push({ start: token.start, end: token.end });
    run = run
      ? { from: run.from, to: index, length: run.length + 1 }
      : { from: index, to: index, length: 1 };
    if (!longest || run.length > longest.length) {
      longest = run;
    }
  }
  const longestRun =
    longest && longest.length >= MIN_RUN
      ? {
          start: tokens[longest.from].start,
          end: tokens[longest.to].end,
          length: longest.length,
        }
      : null;
  return { words: tokens.length, glueSpans, longestRun };
}

/** Rate every sentence of `text` and the text as a whole. Offsets index the original. */
export function analyseStickiness(
  text: string,
  options: StickyOptions = DEFAULT_STICKY_OPTIONS
): StickyReport {
  const allSentences = splitSentences(text);
  const headings = headingFlags(allSentences);
  const tokens = wordTokens(text);
  const sentences: StickySentence[] = [];
  let totalWords = 0;
  let totalGlue = 0;
  let cursor = 0;
  for (const [index, sentence] of allSentences.entries()) {
    if (headings[index]) {
      continue;
    }
    while (cursor < tokens.length && tokens[cursor].start < sentence.start) {
      cursor += 1;
    }
    let end = cursor;
    while (end < tokens.length && tokens[end].end <= sentence.end) {
      end += 1;
    }
    const counted = countSentence(tokens.slice(cursor, end));
    cursor = end;
    if (counted.words === 0) {
      continue;
    }
    const share = counted.glueSpans.length / counted.words;
    totalWords += counted.words;
    totalGlue += counted.glueSpans.length;
    sentences.push({
      start: sentence.start,
      end: sentence.end,
      text: sentence.text,
      words: counted.words,
      glue: counted.glueSpans.length,
      share,
      level: rate(share, counted.words, options),
      glueSpans: counted.glueSpans,
      longestRun: counted.longestRun,
    });
  }
  return {
    words: totalWords,
    glue: totalGlue,
    index: totalWords > 0 ? totalGlue / totalWords : 0,
    sentences,
    sticky: sentences.filter((s) => s.level === "sticky").length,
    semi: sentences.filter((s) => s.level === "semi").length,
    rated: sentences.filter((s) => s.level !== "short").length,
  };
}
